import { Cell, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";
import { Fragment } from "react";

import { CountingProgressBar } from "@/components/CountingProgressBar";
import { FormatDate } from "@/components/Date";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { getCountingBarSortFunction } from "@/utils/react-table-utils";
import { WorkOrder, WorkOrderLineItem } from "../../queries/tracking-data/work-orders";
import { LineItemDescription } from "../LineItemDescription";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export interface WorkOrderTableRow {
  rowId: string;
  wo: WorkOrder;
  line: WorkOrderLineItem;
}

// For WO columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same receipt
const woColCellProps = (
  cell: Cell<WorkOrderTableRow, unknown>,
  additionalProps?: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan">,
) => {
  const { wo, line } = cell.row.original;
  const firstLineId = wo.jobLines[0]!.id;
  const isFirstLine = line.id === firstLineId;
  return isFirstLine ?
      { ...additionalProps, rowSpan: wo.jobLines.length }
    : { ...additionalProps, className: "d-none " + (additionalProps?.className ?? "") };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<WorkOrderTableRow>();
const woAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: woColCellProps } }, column));
const lineItemAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ enableSorting: false }, column));

const woColumns = [
  woAccessorCol("wo.number", {
    header: "WO #",
    cell: ({ getValue, row }) => <SosEntityLink type="workorder" id={row.original.wo.id} label={getValue()} />,
  }),
  woAccessorCol("wo.date", {
    header: "Date",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  woAccessorCol<"wo.linkedBuilds", WorkOrder["linkedBuilds"]>("wo.linkedBuilds", {
    header: "Linked Builds",
    enableSorting: false,
    cell: ({ row, getValue }) =>
      getValue().map((build, index) => (
        <Fragment key={build.id}>
          {index > 0 && <br />}
          <JobTrackingInternalLink trackerPath="builds">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityLineItemHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="build"
                id={build.id}
                lineNumber={0}
                label={build.number}
              />
            )}
          </JobTrackingInternalLink>
        </Fragment>
      )),
  }),
  woAccessorCol("wo.receivedJobLines", {
    header: "Line Items Produced",
    sortingFn: getCountingBarSortFunction<WorkOrderTableRow>(
      ({ wo }) => wo.receivedJobLines,
      ({ wo }) => wo.jobLines.length,
    ),
    cell: ({ getValue, row }) => <CountingProgressBar now={getValue()} max={row.original.wo.jobLines.length} />,
  }),
  woAccessorCol("wo.closed", {
    header: "Closed",
    cell: ({ getValue }) => (getValue() ? "✅" : "⬜"),
    meta: { cellProps: (cell) => woColCellProps(cell, { className: "text-center" }) },
  }),
  woAccessorCol("wo.archived", {
    header: "Archived",
    cell: ({ getValue }) => (getValue() ? "✅" : "⬜"),
    meta: { cellProps: (cell) => woColCellProps(cell, { className: "text-center" }) },
  }),
];

const lineItemColumns = [
  lineItemAccessorCol("line.lineNumber", {
    header: "Line #",
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: { className: "border-start-double" },
    },
  }),
  lineItemAccessorCol("line.item.name", {
    header: "Item",
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.line.item.id}
            label={row.original.line.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  lineItemAccessorCol("line.item.description", {
    header: "Description",
    cell: ({ row }) => <LineItemDescription line={row.original.line} />,
  }),
  lineItemAccessorCol("line.produced", {
    header: "Produced/Quantity",
    cell: ({ getValue, row }) => (
      <CountingProgressBar
        now={getValue()}
        max={row.original.line.uomQuantity}
        selectedItemUOM={row.original.line.itemUOM}
        baseUOM={row.original.line.item.baseItemUOM?.uom}
      />
    ),
  }),
  lineItemAccessorCol("line.linkedBuilds", {
    header: "Linked Builds",
    cell: ({ row, getValue }) => (
      <>
        {getValue().map((build, index) => (
          <Fragment key={build.id}>
            {index > 0 && <br />}
            <JobTrackingInternalLink trackerPath="builds">
              {({ targetPath, targetPageTitle }) => (
                <SOSEntityLineItemHashLink
                  fromHash={row.id}
                  urlPath={targetPath}
                  tooltipContent={`View on '${targetPageTitle}' page`}
                  type="build"
                  id={build.id}
                  lineNumber={0}
                  label={build.number}
                />
              )}
            </JobTrackingInternalLink>
          </Fragment>
        ))}
      </>
    ),
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const workOrdersTableColumns = [
  columnHelper.group({ header: "Work Orders", columns: woColumns }),
  columnHelper.group({
    header: "Line Items",
    columns: lineItemColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
];
